import { getRegion } from '@/helpers/getRegion';
import { Region } from '@heidi/schema/ml-scribe';

/** Placeholder in `NEXT_PUBLIC_CLINICAL_CODING_API_BASE_URL` (transcription-style). */
export const CLINICAL_CODING_REGION_PLACEHOLDER = '<REGION>';

const LOCALHOST_FALLBACK = 'http://localhost:9002';

/**
 * AWS region codes used in clinical-coding.*.aws.ext.heidi.ai hostnames.
 *
 * Only regions whose SCC stack validates coding JWTs belong here. Unmapped
 * regions fail closed (null) so we never send an AU JWT to the wrong host.
 * US/use1 stays off until CASE_ACCESS_CONTEXT_ENABLED is on for staging use1.
 */
const regionToCodingAwsRegion: Partial<Record<Region, string>> = {
  [Region.AU]: 'aue1',
};

/**
 * Resolves the clinical coding (SCC) API base URL for the current user region.
 *
 * When `NEXT_PUBLIC_CLINICAL_CODING_API_BASE_URL` contains `<REGION>`, substitute
 * the AWS region code from `getRegion()`. Returns `null` when the region has no
 * mapped coding stack (fail closed).
 *
 * Concrete URLs (no placeholder) are returned unchanged for backwards
 * compatibility (localhost, fixed regional hosts).
 */
export function getClinicalCodingApiBaseUrl(fallbackRegion?: Region): string | null {
  const configured =
    process.env.NEXT_PUBLIC_CLINICAL_CODING_API_BASE_URL ?? LOCALHOST_FALLBACK;

  if (!configured.includes(CLINICAL_CODING_REGION_PLACEHOLDER)) {
    return configured;
  }

  const awsRegion = regionToCodingAwsRegion[getRegion(fallbackRegion)];
  if (!awsRegion) {
    return null;
  }

  return configured.replaceAll(CLINICAL_CODING_REGION_PLACEHOLDER, awsRegion);
}
